import TemplateDesigner from '../components/TemplateDesigner';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { templatesAPI, revisionsAPI } from '../utils/api';
import { Template, TemplateSection } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import toast from 'react-hot-toast';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  FileText,
  Eye,
  X,
  Loader2,
  Code,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';

interface TemplateFormData {
  nome: string;
  descrizione: string;
  tipo: 'offerta' | 'fattura';
  contenuto: string;
  variabili: Record<string, string>;
  sezioni: TemplateSection[];
  branding: Record<string, string>;
}

const initialFormData: TemplateFormData = {
  nome: '',
  descrizione: '',
  tipo: 'offerta',
  contenuto: '',
  variabili: {},
  sezioni: [],
  branding: {},
};

export default function Templates() {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('template:create');
  const canEdit = hasPermission('template:edit');
  const canDelete = hasPermission('template:delete');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [tipoFilter, setTipoFilter] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [formData, setFormData] = useState<TemplateFormData>(initialFormData);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [validationResult, setValidationResult] = useState<{ valid: boolean; error?: string } | null>(null);

  // Fetch templates
  const { data: templatesData, isLoading } = useQuery({
    queryKey: ['templates', page, search, tipoFilter],
    queryFn: async () => {
      const response = await templatesAPI.getTemplates({
        page,
        limit: 20,
        search: search || undefined,
        tipo: tipoFilter || undefined,
      });
      return response.data;
    },
  });

  // Create template mutation
  const createMutation = useMutation({
    mutationFn: (data: TemplateFormData) => templatesAPI.createTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      toast.success('Template creato con successo');
      closeModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    },
  });

  // Update template mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: TemplateFormData }) =>
      templatesAPI.updateTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      toast.success('Template aggiornato con successo');
      closeModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    },
  });

  // Delete template mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => templatesAPI.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      toast.success('Template eliminato con successo');
      setDeleteConfirm(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    },
  });

  // Validate template mutation
  const validateMutation = useMutation({
    mutationFn: (contenuto: string) => templatesAPI.validate({ contenuto, nome: 'test', tipo: 'offerta' } as any),
    onSuccess: (response) => {
      setValidationResult(response.data);
    },
    onError: () => {
      setValidationResult({ valid: false, error: 'Errore durante la validazione' });
    },
  });

  const openCreateModal = () => {
    setEditingTemplate(null);
    setFormData({
      ...initialFormData,
      contenuto: '<html><body><h1>{{ offerta.titolo }}</h1><p>Offerta {{ offerta.numero }} · Revisione {{ offerta.revisione }}</p><h2>Cliente</h2><p>{{ cliente.denominazione_completa }}</p>{{ items_table }}{{ totals }}{{ sections_html }}</body></html>',
      sezioni: [{key:'proposta', titolo:'Proposta', tipo:'libera', formato:'html', origine:'note', obbligatoria:false, contenuto:''}],
    });
    setValidationResult(null);
    setIsModalOpen(true);
  };

  const openEditModal = (template: Template) => {
    setEditingTemplate(template);
    setFormData({
      nome: template.nome,
      descrizione: template.descrizione || '',
      tipo: template.tipo as 'offerta' | 'fattura',
      contenuto: template.contenuto,
      variabili: template.variabili || {},
      sezioni: template.sezioni || [],
      branding: template.branding || {},
    });
    setValidationResult(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingTemplate(null);
    setFormData(initialFormData);
    setValidationResult(null);
  };

  const handleValidate = () => {
    validateMutation.mutate(formData.contenuto);
  };

  const handlePreview = (template: Template) => {
    setPreviewContent(template.contenuto);
    setIsPreviewOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingTemplate) {
      updateMutation.mutate({ id: editingTemplate.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const templates = templatesData?.data || [];
  const pagination = templatesData?.pagination;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Templates</h1>
          <p className="text-gray-600">Gestisci i template per offerte e documenti</p>
        </div>
        {canCreate && (
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-5 w-5" />
            Nuovo Template
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca per nome..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={tipoFilter}
            onChange={(e) => {
              setTipoFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Tutti i tipi</option>
            <option value="offerta">Offerta</option>
            <option value="fattura">Fattura</option>
          </select>
        </div>
      </div>

      {/* Templates List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Nessun template trovato</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
            {templates.map((template) => (
              <div
                key={template.id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="h-8 w-8 text-blue-500" />
                    <div>
                      <h3 className="font-medium text-gray-900">{template.nome}</h3>
                      <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                        {template.tipo === 'offerta' ? 'Offerta' : 'Fattura'}
                      </span>
                    </div>
                  </div>
                </div>
                {template.descrizione && (
                  <p className="mt-2 text-sm text-gray-600 line-clamp-2">{template.descrizione}</p>
                )}
                <div className="mt-4 flex justify-end gap-2">
                  {canEdit && <button type="button" className="rounded border px-3 text-sm" onClick={async () => {
                    try { const result = await revisionsAPI.publish(template.id); toast.success(`Pubblicata versione ${result.data.versione}`); }
                    catch { toast.error('Pubblicazione non riuscita'); }
                  }}>Pubblica versione</button>}
                  <button
                    onClick={() => handlePreview(template)}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                    title="Anteprima"
                  >
                    <Eye className="h-5 w-5" />
                  </button>
                  {canEdit && (
                    <button
                      onClick={() => openEditModal(template)}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                      title="Modifica"
                    >
                      <Edit2 className="h-5 w-5" />
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => setDeleteConfirm(template.id)}
                      className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      title="Elimina"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Pagina {pagination.page} di {pagination.pages} ({pagination.total} templates)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Precedente
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= pagination.pages}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Successivo
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Template Form Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold">
                {editingTemplate ? 'Modifica Template' : 'Nuovo Template'}
              </h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Nome e Tipo */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                  <select
                    value={formData.tipo}
                    onChange={(e) =>
                      setFormData({ ...formData, tipo: e.target.value as 'offerta' | 'fattura' })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="offerta">Offerta</option>
                    <option value="fattura">Fattura</option>
                  </select>
                </div>
              </div>

              {/* Descrizione */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
                <input
                  type="text"
                  value={formData.descrizione}
                  onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Contenuto Template */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Layout del modello *
                  </label>
                  <button
                    type="button"
                    onClick={handleValidate}
                    disabled={validateMutation.isPending}
                    className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                  >
                    {validateMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Code className="h-4 w-4" />
                    )}
                    Valida sintassi
                  </button>
                </div>
                <TemplateDesigner html={formData.contenuto} onChange={contenuto => setFormData(previous => ({...previous, contenuto}))}
                  onImport={(contenuto, branding) => setFormData(previous => ({...previous, contenuto, branding: {...previous.branding, ...branding}, sezioni: previous.sezioni.length ? previous.sezioni : [{key:'proposta', titolo:'Proposta', tipo:'libera', formato:'html', origine:'note', obbligatoria:false, contenuto:''}]}))} />
                <fieldset className="space-y-3 rounded border p-3">
                  <legend>Sezioni del template</legend>
                  {formData.sezioni.map((section, index) => {
                    const change = (patch: Partial<TemplateSection>) => setFormData({ ...formData, sezioni: formData.sezioni.map((v, i) => i === index ? { ...v, ...patch } : v) });
                    return <div key={index} className="space-y-2 border-b pb-3">
                      <input aria-label="Identificativo sezione" required pattern="[a-zA-Z][a-zA-Z0-9_]*" value={section.key} onChange={e => change({key: e.target.value})} className="rounded border p-2" />
                      <input aria-label="Titolo sezione" required value={section.titolo} onChange={e => change({titolo: e.target.value})} className="rounded border p-2" />
                      <select aria-label="Tipo sezione" value={section.tipo} onChange={e => change({tipo: e.target.value as TemplateSection['tipo']})} className="rounded border p-2"><option value="fissa">Fissa</option><option value="automatica">Automatica</option><option value="libera">Libera</option></select>
                      <label><input type="checkbox" checked={section.obbligatoria} onChange={e => change({obbligatoria: e.target.checked})} /> Obbligatoria</label>
                      {section.tipo === 'libera' && <div className="flex flex-wrap gap-3">
                        <label>Scrittura <select aria-label="Formato sezione" value={section.formato || 'testo'} onChange={e => change({formato: e.target.value as 'testo' | 'html'})} className="rounded border p-2"><option value="html">Testo formattato, tabelle e immagini</option><option value="testo">Solo testo</option></select></label>
                        <label>Testo iniziale <select aria-label="Origine testo sezione" value={section.origine || ''} onChange={e => change({origine: (e.target.value || undefined) as TemplateSection['origine']})} className="rounded border p-2"><option value="">Contenuto del modello</option><option value="note">Note originali dell’offerta</option><option value="descrizione">Descrizione dell’offerta</option></select></label>
                      </div>}
                      <label className="block"><input type="checkbox" checked={section.mostra_titolo !== false} onChange={e => change({mostra_titolo: e.target.checked})} /> Mostra titolo nel documento</label>
                      <textarea aria-label="Contenuto iniziale sezione" value={section.contenuto} onChange={e => change({contenuto: e.target.value})} className="w-full rounded border p-2" />
                      <button type="button" onClick={() => setFormData({...formData, sezioni: formData.sezioni.filter((_, i) => i !== index)})}>Rimuovi sezione</button>
                    </div>;
                  })}
                  <button type="button" onClick={() => setFormData({...formData, sezioni: [...formData.sezioni, {key: `sezione_${formData.sezioni.length + 1}`, titolo: 'Nuova sezione', tipo: 'libera', formato: 'html', obbligatoria: false, contenuto: ''}]})}>Aggiungi sezione</button>
                  <label className="block">Colore principale <input type="color" value={formData.branding.colore_primario || '#2563eb'} onChange={e => setFormData({...formData, branding: {...formData.branding, colore_primario: e.target.value}})} /></label>
                  <label className="block">Carattere <select value={formData.branding.font || 'Arial'} onChange={e => setFormData({...formData, branding: {...formData.branding, font:e.target.value}})}>{['Calibri','Arial','Georgia','Verdana'].map(f => <option key={f}>{f}</option>)}</select></label>
                  <label className="block">Piè di pagina <input value={formData.branding.footer || ''} onChange={e => setFormData({...formData, branding: {...formData.branding, footer:e.target.value}})} className="w-full rounded border p-2" /></label>

                </fieldset>
                {validationResult && (
                  <div
                    className={`mt-2 p-3 rounded-lg flex items-center gap-2 ${
                      validationResult.valid
                        ? 'bg-green-50 text-green-800'
                        : 'bg-red-50 text-red-800'
                    }`}
                  >
                    {validationResult.valid ? (
                      <>
                        <CheckCircle className="h-5 w-5" />
                        Sintassi valida
                      </>
                    ) : (
                      <>
                        <AlertCircle className="h-5 w-5" />
                        {validationResult.error}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Variabili disponibili */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Variabili disponibili:</h4>
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                  <div>
                    <strong>tenant:</strong> nome, ragione_sociale, indirizzo, partita_iva, telefono,
                    email, colore_primario
                  </div>
                  <div>
                    <strong>cliente:</strong> denominazione_completa, nome, cognome, ragione_sociale,
                    indirizzo, partita_iva
                  </div>
                  <div>
                    <strong>offerta:</strong> numero, titolo, descrizione, importo_totale,
                    importo_imponibile, importo_iva, aliquota_iva, note
                  </div>
                  <div>
                    <strong>items:</strong> descrizione, quantita, prezzo_unitario, sconto_percentuale,
                    importo_totale
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annulla
                </button>
                {(editingTemplate ? canEdit : canCreate) && (
                  <button
                    type="submit"
                    disabled={createMutation.isPending || updateMutation.isPending}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createMutation.isPending || updateMutation.isPending ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : editingTemplate ? (
                      'Aggiorna'
                    ) : (
                      'Crea'
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {isPreviewOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">Anteprima Template (Codice)</h2>
              <button
                onClick={() => setIsPreviewOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[70vh]">
              <pre className="bg-gray-50 p-4 rounded-lg text-sm overflow-x-auto">
                <code>{previewContent}</code>
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Conferma Eliminazione</h3>
            <p className="text-gray-600 mb-6">
              Sei sicuro di voler eliminare questo template? L'operazione non può essere annullata.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirm)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  'Elimina'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
